import { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Input, Modal, Popconfirm, Slider } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripVertical,
  faImage,
  faPen,
  faTrashCan,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";

import {
  ACCEPTED_IMAGE_TYPES,
  DEFAULT_FOV,
  MAX_FOV,
  MIN_FOV,
  PITCH_LIMIT_DEG,
  SCENE_PROJECTIONS,
  YAW_LIMIT_DEG,
  is180Scene,
} from "../utils/tourEditorUtils";
import styles from "./TourEditorSections.module.css";

export default function ScenesSidebarSection({
  scenes,
  activeSceneId,
  onSelectScene,
  onAddScenes,
  onAddVideoScene,
  onRenameScene,
  onDeleteScene,
  onReorderScenes,
  onUpdateSceneView,
  onResetCamera,
  uploading,
}) {
  const imageInputRef = useRef(null);
  const image180InputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const [canDragId, setCanDragId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const touchStateRef = useRef(null);

  useEffect(() => {
    if (canDragId === null) return;
    const handlePointerUp = () => setCanDragId(null);
    window.addEventListener("pointerup", handlePointerUp);
    return () => window.removeEventListener("pointerup", handlePointerUp);
  }, [canDragId]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex === null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const pos = e.clientY < midY ? "before" : "after";
    if (dragOverIndex !== index || dropPosition !== pos) {
      setDragOverIndex(index);
      setDropPosition(pos);
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || typeof onReorderScenes !== "function") return;
    const fromIndex = draggedIndex;
    if (targetIndex !== null && dropPosition) {
      let toIndex = targetIndex;
      if (dropPosition === "before") {
        toIndex = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
      } else if (dropPosition === "after") {
        toIndex = targetIndex < fromIndex ? targetIndex + 1 : targetIndex;
      }
      if (toIndex !== fromIndex) {
        onReorderScenes(fromIndex, toIndex);
      }
    }
    handleDragEnd();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDropPosition(null);
    setCanDragId(null);
  };

  const handleListDrop = e => {
    if (
      e.target === e.currentTarget &&
      draggedIndex !== null &&
      typeof onReorderScenes === "function"
    ) {
      e.preventDefault();
      const lastIndex = scenes.length - 1;
      if (draggedIndex !== lastIndex && lastIndex >= 0) {
        onReorderScenes(draggedIndex, lastIndex);
      }
      handleDragEnd();
    }
  };

  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStateRef.current = {
      fromIndex: index,
      startX: touch.clientX,
      startY: touch.clientY,
    };
    setDraggedIndex(index);
    setCanDragId(scenes[index]?.id);
  };

  const handleTouchMove = e => {
    if (!touchStateRef.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const targetItem = element?.closest("[data-scene-index]");
    if (targetItem) {
      const targetIndex = Number(targetItem.getAttribute("data-scene-index"));
      if (
        Number.isInteger(targetIndex) &&
        targetIndex >= 0 &&
        targetIndex < scenes.length
      ) {
        const rect = targetItem.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const pos = touch.clientY < midY ? "before" : "after";
        setDragOverIndex(targetIndex);
        setDropPosition(pos);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!touchStateRef.current) return;
    const fromIndex = touchStateRef.current.fromIndex;
    touchStateRef.current = null;
    if (
      dragOverIndex !== null &&
      dropPosition &&
      typeof onReorderScenes === "function"
    ) {
      let toIndex = dragOverIndex;
      if (dropPosition === "before") {
        toIndex = dragOverIndex > fromIndex ? dragOverIndex - 1 : dragOverIndex;
      } else if (dropPosition === "after") {
        toIndex = dragOverIndex < fromIndex ? dragOverIndex + 1 : dragOverIndex;
      }
      if (toIndex !== fromIndex) {
        onReorderScenes(fromIndex, toIndex);
      }
    }
    handleDragEnd();
  };

  const handleTouchCancel = () => {
    touchStateRef.current = null;
    handleDragEnd();
  };

  const handleKeyDown = (e, index) => {
    if (typeof onReorderScenes !== "function") return;
    if (e.key === "ArrowUp" && index > 0) {
      e.preventDefault();
      onReorderScenes(index, index - 1);
    } else if (e.key === "ArrowDown" && index < scenes.length - 1) {
      e.preventDefault();
      onReorderScenes(index, index + 1);
    }
  };

  const startRename = scene => {
    setEditingSceneId(scene.id);
    setEditingName(scene.name);
  };

  const commitRename = () => {
    if (editingSceneId) {
      onRenameScene(editingSceneId, editingName);
      setEditingSceneId(null);
      setEditingName("");
    }
  };

  const handleImageFileChange = event => {
    const files = event.target.files;
    if (files?.length && typeof onAddScenes === "function") {
      onAddScenes(files, { projection: SCENE_PROJECTIONS.EQUIRECTANGULAR_360 });
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handle180ImageFileChange = event => {
    const files = event.target.files;
    if (files?.length && typeof onAddScenes === "function") {
      onAddScenes(files, { projection: SCENE_PROJECTIONS.EQUIRECTANGULAR_180 });
    }
    if (image180InputRef.current) image180InputRef.current.value = "";
  };

  const handleVideoFileChange = event => {
    const files = event.target.files;
    if (files?.length && typeof onAddVideoScene === "function") {
      onAddVideoScene(files[0]);
    }
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const addMenuItems = [
    {
      key: "image",
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faImage} style={{ width: 14 }} />
          <span>360° Image</span>
        </span>
      ),
      onClick: () => imageInputRef.current?.click(),
    },
    {
      key: "image180",
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faImage} style={{ width: 14 }} />
          <span>180° Image</span>
        </span>
      ),
      onClick: () => image180InputRef.current?.click(),
    },
    {
      key: "video",
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faVideo} style={{ width: 14 }} />
          <span>360° Video</span>
        </span>
      ),
      onClick: () => videoInputRef.current?.click(),
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3>Scenes</h3>
        <Dropdown
          menu={{ items: addMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
          disabled={Boolean(uploading)}
        >
          <Button
            type="link"
            className={styles.addBtn}
            loading={Boolean(uploading)}
          >
            + Add
          </Button>
        </Dropdown>
        <input
          ref={imageInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple
          className={styles.hiddenInput}
          onChange={handleImageFileChange}
          aria-label="Upload 360 panorama image scenes"
        />
        <input
          ref={image180InputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple
          className={styles.hiddenInput}
          onChange={handle180ImageFileChange}
          aria-label="Upload 180 panorama image scenes"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4"
          className={styles.hiddenInput}
          onChange={handleVideoFileChange}
          aria-label="Upload 360 video scene"
        />
      </div>

      {uploading && <p className={styles.hint}>Uploading...</p>}

      <div
        className={styles.list}
        onDragOver={e => {
          if (draggedIndex !== null) e.preventDefault();
        }}
        onDrop={handleListDrop}
      >
        {scenes.map((scene, index) => {
          const isCurrentDragging = draggedIndex === index;
          const isCurrentOver = dragOverIndex === index && !isCurrentDragging;
          const dropIndicatorClass = isCurrentOver
            ? dropPosition === "before"
              ? styles.dropIndicatorTop
              : styles.dropIndicatorBottom
            : "";

          return (
            <div
              key={scene.id}
              data-scene-index={index}
              draggable={canDragId === scene.id}
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`${styles.listItem} ${
                activeSceneId === scene.id ? styles.listItemActive : ""
              } ${isCurrentDragging ? styles.draggingItem : ""} ${dropIndicatorClass}`}
            >
              <div className={styles.listItemTopRow}>
                <button
                  type="button"
                  className={styles.dragHandle}
                  aria-label={`Drag to reorder ${scene.name}. Use arrow keys to reorder.`}
                  title="Drag to reorder"
                  onPointerDown={() => setCanDragId(scene.id)}
                  onPointerUp={() => setCanDragId(null)}
                  onTouchStart={e => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                  onKeyDown={e => handleKeyDown(e, index)}
                >
                  <FontAwesomeIcon icon={faGripVertical} />
                </button>
                <button
                  type="button"
                  className={styles.listItemMain}
                  onClick={() => onSelectScene(scene.id)}
                  title={index === 0 ? `${scene.name} (Start Scene)` : scene.name}
                >
                  <span className={styles.listItemLabel}>{scene.name}</span>
                  {scene.mediaType === "video" && (
                    <span className={styles.videoBadge} title="360° Video Scene">
                      <FontAwesomeIcon icon={faVideo} style={{ marginRight: 3, fontSize: "0.55rem" }} />
                      VIDEO
                    </span>
                  )}
                  {is180Scene(scene) && (
                    <span className={styles.badge180} title="180° Panorama Scene">
                      180°
                    </span>
                  )}
                  {index === 0 && (
                    <span className={styles.startBadge} title="Tour starting scene">
                      START
                    </span>
                  )}
                </button>
                <div className={styles.listItemActions}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    aria-label={`Rename ${scene.name}`}
                    onClick={() => startRename(scene)}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <Popconfirm
                    title="Delete this scene?"
                    description="Hotspots targeting this scene will be cleared."
                    onConfirm={() => onDeleteScene(scene.id)}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      aria-label={`Delete ${scene.name}`}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </Popconfirm>
                </div>
              </div>
            {activeSceneId === scene.id && (
              <div className={styles.listItemDetails}>
                <div className={styles.detailLabel}>
                  <span>
                    Rotation Offset (Yaw)
                    {is180Scene(scene) && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: "0.6rem",
                          color: "var(--xr-text-muted, #718096)",
                          textTransform: "none",
                          fontWeight: 400,
                        }}
                      >
                        (Fixed 0°)
                      </span>
                    )}
                  </span>
                  <Button
                    type="link"
                    size="small"
                    className={styles.addBtn}
                    style={{ padding: 0, fontSize: "0.65rem" }}
                    onClick={onResetCamera}
                  >
                    Preview Start View
                  </Button>
                </div>
                <div onPointerDownCapture={() => !is180Scene(scene) && onResetCamera?.()}>
                  <Slider
                    min={-YAW_LIMIT_DEG}
                    max={YAW_LIMIT_DEG}
                    value={is180Scene(scene) ? 0 : scene.defaultView?.yaw || 0}
                    disabled={is180Scene(scene)}
                    onChange={val => onUpdateSceneView(scene.id, { yaw: val })}
                    tooltip={{
                      formatter: val =>
                        is180Scene(scene) ? "0° (Fixed for 180°)" : `${val}°`,
                    }}
                    className={styles.slider}
                  />
                </div>

                <div className={styles.detailLabel}>
                  <span>Vertical Offset (Pitch)</span>
                </div>
                <div onPointerDownCapture={() => onResetCamera?.()}>
                  <Slider
                    min={-PITCH_LIMIT_DEG}
                    max={PITCH_LIMIT_DEG}
                    value={scene.defaultView?.pitch || 0}
                    onChange={val => onUpdateSceneView(scene.id, { pitch: val })}
                    tooltip={{ formatter: val => `${val}°` }}
                    className={styles.slider}
                  />
                </div>

                <div className={styles.detailLabel}>
                  <span>Zoom</span>
                </div>
                <div onPointerDownCapture={() => onResetCamera?.()}>
                  <Slider
                    min={MIN_FOV}
                    max={MAX_FOV}
                    reverse
                    value={scene.defaultView?.fov ?? DEFAULT_FOV}
                    onChange={val => onUpdateSceneView(scene.id, { fov: val })}
                    tooltip={{ formatter: val => `${val}°` }}
                    className={styles.slider}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
        {!scenes.length && (
          <p className={styles.emptyState}>No scenes yet</p>
        )}
      </div>

      <Modal
        title="Rename Scene"
        open={Boolean(editingSceneId)}
        onOk={commitRename}
        onCancel={() => setEditingSceneId(null)}
        okText="Save"
      >
        <Input
          value={editingName}
          onChange={event => setEditingName(event.target.value)}
          placeholder="Scene name"
          onPressEnter={commitRename}
        />
      </Modal>
    </section>
  );
}
